import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { LogIn, User, Star, MapPin, ChevronDown, Shield, LogOut, Settings, Building2 } from "lucide-react";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const FloatingUserMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [linkedCenterLogo, setLinkedCenterLogo] = useState(null);
  const menuRef = useRef(null);

  // Helper to get user from either storage
  const getUserFromStorage = () => {
    return JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "null");
  };

  const fetchLinkedCenterLogo = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) return;
      
      const res = await axios.get(`${API}/users/me/linked-dive-center`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.logo_url) {
        setLinkedCenterLogo(res.data.logo_url);
      }
    } catch (error) {
      console.error("Failed to fetch linked center logo:", error);
    }
  }, []);

  useEffect(() => {
    setMounted(true);
    const currentUser = getUserFromStorage();
    setUser(currentUser);
    
    // Fetch linked dive center logo for dive_center accounts
    if (currentUser?.role === "dive_center" && currentUser?.linked_dive_center_id) {
      fetchLinkedCenterLogo();
    }
  }, [location, fetchLinkedCenterLogo]); // Refresh on location change

  // Also listen for storage events (login from other tabs/windows)
  useEffect(() => {
    const handleStorageChange = () => {
      const currentUser = getUserFromStorage();
      setUser(currentUser);
      
      // Fetch linked dive center logo for dive_center accounts
      if (currentUser?.role === "dive_center" && currentUser?.linked_dive_center_id) {
        fetchLinkedCenterLogo();
      } else {
        setLinkedCenterLogo(null);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Custom event for same-window login
    window.addEventListener('userLogin', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogin', handleStorageChange);
    };
  }, [fetchLinkedCenterLogo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavigation = (path) => {
    setIsOpen(false);
    // If already on the target page, just close dropdown
    if (window.location.pathname === path) {
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    setIsOpen(false);
    navigate("/");
    window.location.reload(); // Refresh to update all components
  };

  // Don't render until mounted (needed for portal)
  if (!mounted) return null;

  const menuContent = !user ? (
    // Show login button if not logged in
    <button
      onClick={() => navigate("/account")}
      className="fixed top-20 right-6 w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white shadow-lg flex items-center justify-center transition-all hover:scale-110 z-[9990]"
      aria-label="Login"
    >
      <LogIn className="w-6 h-6" />
    </button>
  ) : (
    // Show dropdown menu for logged-in users
    <div className="fixed top-20 right-6 z-[9990]" ref={menuRef}>
      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-16 right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-[9991]">
          <button
            onClick={() => handleNavigation(`/profile/${user.id}`)}
            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors text-gray-700"
          >
            <User className="w-5 h-5 text-cyan-500" />
            <span className="font-medium">My Profile</span>
          </button>
          
          <button
            onClick={() => handleNavigation('/account')}
            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors text-gray-700 border-t border-gray-100"
          >
            <Settings className="w-5 h-5 text-gray-500" />
            <span className="font-medium">Account Settings</span>
          </button>
          
          {user.role === "admin" && (
            <button
              onClick={() => handleNavigation('/admin')}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors text-gray-700 border-t border-gray-100"
            >
              <Shield className="w-5 h-5 text-red-500" />
              <span className="font-medium">Admin Dashboard</span>
            </button>
          )}
          
          {/* Hide Submit Review and Dive Report for dive_center accounts */}
          {user.role !== "dive_center" && (
            <>
              <button
                onClick={() => handleNavigation('/submit-review')}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors text-gray-700 border-t border-gray-100"
              >
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="font-medium">Submit Review</span>
              </button>
              
              <button
                onClick={() => handleNavigation('/dive-report')}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors text-gray-700 border-t border-gray-100"
              >
                <MapPin className="w-5 h-5 text-cyan-500" />
                <span className="font-medium">Dive Report</span>
              </button>
            </>
          )}
          
          <button
            onClick={handleLogout}
            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 transition-colors text-red-600 border-t border-gray-100"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Log Out</span>
          </button>
        </div>
      )}

      {/* Toggle Button with Profile Picture or Dive Center Logo */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white shadow-lg flex items-center justify-center transition-all hover:scale-110 overflow-hidden"
        aria-label="User Menu"
      >
        {/* For dive_center accounts, prefer linked center logo with blue background */}
        {user.role === "dive_center" && linkedCenterLogo ? (
          <div className="w-full h-full bg-gradient-to-r from-cyan-500 to-blue-600 p-1.5 flex items-center justify-center">
            <img 
              src={linkedCenterLogo.startsWith('http') ? linkedCenterLogo : `${BACKEND_URL}${linkedCenterLogo}`} 
              alt={user.name} 
              className="w-full h-full object-contain rounded-full"
            />
          </div>
        ) : user.profile_pic ? (
          <img 
            src={getImageUrl(user.profile_pic)} 
            alt={user.name} 
            className="w-full h-full object-cover"
          />
        ) : user.role === "dive_center" ? (
          <Building2 className="w-6 h-6" />
        ) : isOpen ? (
          <ChevronDown className="w-6 h-6" />
        ) : (
          <User className="w-6 h-6" />
        )}
      </button>
    </div>
  );

  // Render via portal to document.body for consistent stacking context
  return createPortal(menuContent, document.body);
};

export default FloatingUserMenu;
